import Alert from '@amzn/meridian/alert';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Link from '@amzn/meridian/link';
import Loader from '@amzn/meridian/loader';
import Modal from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Toaster from '@amzn/meridian/toaster';
import { Field, Form, Formik, FormikProps } from 'formik';
import React, { useCallback, useState } from 'react';
import { FormattedMessage, IntlShape, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router';

import DataSource from '../../../api';
import {
  CoachingFormInputData,
  DeliveredStatusObject,
  NoCoachReasonsObject,
} from '../../../api/CoachingForm/schema';
import DataState from '../../../Types/DataState';
import { renderUrl, urls } from '../../../config/Url';
import { getLabelFromValue } from '../FieldComponents/Helpers';
import Select from '../FieldComponents/Select';
import Textarea from '../FieldComponents/Textarea';
import { DisplayObject } from '../LayoutComponents/FieldArrayDisplay';
import trimCoachingData from '../trimCoachingData';
import validationSchema from './Context';
import { getAllReasonOptions, getReasonOptions } from './Data';
import { COACHING_TEXT_LIMIT } from '../../../Util';

export const convertReasonToDisplay = (
  noCoach: CoachingFormInputData,
  intl: IntlShape
): DisplayObject[] => {
  const { reason, otherReason } = noCoach;

  // Reason
  const display = [
    {
      label: intl.formatMessage({
        id: 'cs_perfportal_coaching_nosession_label_readonly_reason',
        defaultMessage: 'Reason:',
      }),
      value: getLabelFromValue(reason, getAllReasonOptions(intl)),
    },
  ];

  // Description
  if (otherReason)
    display.push({
      label: intl.formatMessage({
        id: 'cs_perfportal_coaching_nosession_label_readonly_other_reason_description',
        defaultMessage: 'Description:',
      }),
      value: otherReason,
    });

  return display;
};

export const FormContent: React.FC<FormikProps<CoachingFormInputData>> = ({
  values,
  errors,
  touched,
  handleSubmit,
  handleReset,
}: FormikProps<CoachingFormInputData>): React.ReactElement => {
  const intl = useIntl();

  return (
    <Form>
      <Column>
        <Field
          as={Select}
          name='reason'
          options={getReasonOptions(useIntl())}
          label={intl.formatMessage({
            id: 'cs_perfportal_coaching_nosession_label_required_reason',
            defaultMessage: 'Reason *',
          })}
          errorText={touched.reason && errors.reason}
        />
        {values.reason === NoCoachReasonsObject.OTHER && (
          <Field
            as={Textarea}
            name='otherReason'
            label={intl.formatMessage({
              id: 'cs_perfportal_coaching_nosession_label_required_other_reason_description',
              defaultMessage: 'Describe reason for not coaching *',
            })}
            maxLength={COACHING_TEXT_LIMIT}
            errorText={touched.otherReason && errors.otherReason}
          />
        )}
        <Row alignmentHorizontal='end' widths='fit' spacingInset='400 none'>
          <Button
            data-csa-c-type='button'
            data-csa-c-slot-id='coaching-form-no-coaching-button-slot'
            data-csa-c-content-id='coaching-form-no-coaching-cancel-button'
            type='secondary'
            size='small'
            onClick={handleReset}
          >
            <FormattedMessage
              id='cs_perfportal_cancel'
              defaultMessage='Cancel'
            />
          </Button>
          <Button
            data-csa-c-type='button'
            data-csa-c-slot-id='coaching-form-no-coaching-button-slot'
            data-csa-c-content-id='coaching-form-no-coaching-submit-button'
            type='primary'
            size='small'
            href='/#'
            onClick={handleSubmit}
            submit
          >
            <FormattedMessage
              id='cs_perfportal_submit'
              defaultMessage='Submit'
            />
          </Button>
        </Row>
      </Column>
    </Form>
  );
};

interface ModalFormProps {
  onsubmit: Function;
  onreset: () => void;
  initialValues: CoachingFormInputData;
}

const ModalFormWrapper = ({
  onsubmit,
  onreset,
  initialValues,
}: ModalFormProps): React.ReactElement => {
  const handleSubmit = (values: CoachingFormInputData): void => {
    onsubmit(values);
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema(useIntl())}
      onSubmit={handleSubmit}
      onReset={onreset}
    >
      {(props: FormikProps<CoachingFormInputData>): React.ReactElement => (
        // eslint-disable-next-line react/jsx-props-no-spreading
        <FormContent {...props} />
      )}
    </Formik>
  );
};

interface NoCoachingSessionModalFormProps {
  initialValues: CoachingFormInputData;
  firstName: string;
  lastName: string;
  userName: string;
}

const NoCoachingSessionModalForm: React.FC<NoCoachingSessionModalFormProps> = ({
  initialValues,
  firstName,
  lastName,
  userName,
}: NoCoachingSessionModalFormProps): React.ReactElement => {
  const intl = useIntl();
  const [open, setOpen] = useState(false);
  const onClick = useCallback(() => setOpen(true), []);
  const onClickCancel = useCallback(() => setOpen(false), []);

  const navigate = useNavigate();

  const [submitState, setSubmitState] = useState('');

  const [showToast, setShowToast] = useState(false);

  const noSessiontoast = [
    {
      id: 'No_Coaching_Toast',
      timeout: 2000,
    },
  ];
  const { userId } = useParams() as { userId: string };

  const apiNaws = DataSource.getDataFeed('NAWS');

  const onCloseToast = useCallback(() => {
    setShowToast(false);
  }, []);

  const windowHref = (): void => {
    navigate(renderUrl(`${urls.coachingNotes}/${userId}`), { replace: true });
  };

  const delayRedirect = (): void => {
    windowHref();
  };

  const handleSubmit = useCallback((values: any) => {
    setShowToast(true);
    const newValues = {
      ...values,
      isDelivered: DeliveredStatusObject.NOCOACH,
      coachingDetails: [],
      contactReview: [],
      coachingSession: {
        coachingSummaryNotes: '',
        managerNotes: '',
      },
    };
    const trimmedValues = trimCoachingData(newValues);
    apiNaws.updateCoachingNote(trimmedValues, setSubmitState);

    setOpen(false);

    window.setTimeout(() => {
      delayRedirect();
    }, 2000);
  }, []);
  const onClose = useCallback(() => setOpen(false), []);

  if (submitState === DataState.LOADING) {
    return (
      <div style={{ textAlign: 'center' }}>
        <Loader type='circular' size='large' />
      </div>
    );
  }

  return (
    <Row>
      <Link
        data-csa-c-type='element'
        data-csa-c-slot-id='coaching-form-no-coaching-link-slot'
        data-csa-c-content-id='coaching-form-no-coaching-on-session-link'
        onClick={onClick}
        type='secondary'
      >
        <FormattedMessage
          id='cs_perfportal_coaching_nosession_unable_to_coach_csa'
          defaultMessage="I'm unable to coach this associate"
          description='No Coaching On Session'
        />
      </Link>
      <Modal
        title={intl.formatMessage(
          {
            id: 'cs_perfportal_coaching_nosession_title_no_coaching_for_name_login',
            defaultMessage: `No coaching for {firstName} {lastName} ({login})`,
          },
          {
            firstName,
            lastName,
            login: userName,
          }
        )}
        open={open}
        onClose={onClose}
        scrollContainer='viewport'
        closeLabel='Close'
        aria-describedby='modal-no-coaching-submit-reason'
        bodySpacingInset='none 400 400'
        width='515px'
      >
        <Row spacingInset='400 none'>
          <Text id='modal-no-coaching-submit-reason'>
            <FormattedMessage
              id='cs_perfportal_coaching_nosession_announcement_not_affect_draft'
              defaultMessage='This will not affect your existing coaching draft'
              description='No Coaching Form on Session Modal Message'
            />
          </Text>
        </Row>
        <ModalFormWrapper
          onsubmit={handleSubmit}
          onreset={onClickCancel}
          initialValues={initialValues}
        />
      </Modal>
      {showToast && noSessiontoast !== undefined && (
        <Toaster
          toasts={noSessiontoast}
          onCloseToast={onCloseToast}
          alignmentHorizontal='center'
        >
          {(): React.ReactElement => (
            <Alert toast>
              {submitState === DataState.LOADED && (
                <FormattedMessage
                  id='cs_perfportal_coaching_delivered_no_session_success'
                  defaultMessage='No coaching session has submitted.'
                />
              )}
              {submitState === DataState.ERROR && (
                <FormattedMessage
                  id='cs_perfportal_error_reason_coaching_expired'
                  defaultMessage='This coaching has expired. Please cut us a ticket to delete it and start a new one.'
                />
              )}
            </Alert>
          )}
        </Toaster>
      )}
    </Row>
  );
};

export default NoCoachingSessionModalForm;
